import React, { useState, useEffect } from 'react'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { DocumentQueueManager, DocumentChangeMessage, QueueStats } from '../utils/documentQueueManager'
import { Loader2, Play, Square, RefreshCw, Info, Clock, CheckCircle, XCircle } from 'lucide-react'
import { Alert, AlertDescription } from './ui/alert'
import { supabase } from '../integrations/supabase/client'

export const DocumentQueueMonitor: React.FC = () => {
  const [queueStats, setQueueStats] = useState<QueueStats[]>([])
  const [recentMessages, setRecentMessages] = useState<DocumentChangeMessage[]>([])
  const [isManualConsumerRunning, setIsManualConsumerRunning] = useState(false)
  const [loading, setLoading] = useState(false)
  const [consumerStop, setConsumerStop] = useState<(() => void) | null>(null)
  const [scheduledJobs, setScheduledJobs] = useState<any[]>([])
  const [httpResponses, setHttpResponses] = useState<any[]>([])

  // Load queue stats
  const loadQueueStats = async () => {
    try {
      const stats = await DocumentQueueManager.getQueueStats()
      setQueueStats(stats)
    } catch (error) {
      console.error('Failed to load queue stats:', error)
    }
  }

  // Read recent messages without processing them (for display only)
  const loadRecentMessages = async () => {
    try {
      const messages = await DocumentQueueManager.readDocumentChanges(5, 10)
      setRecentMessages(messages)
    } catch (error) {
      console.error('Failed to load recent messages:', error)
    }
  }

  // Load scheduled jobs status
  const loadScheduledJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('document_queue_schedule_status')
        .select('*')
      
      if (error) throw error
      setScheduledJobs(data || [])
    } catch (error) {
      console.error('Failed to load scheduled jobs:', error)
    }
  }

  // Load recent HTTP responses
  const loadHttpResponses = async () => {
    try {
      const { data, error } = await supabase
        .from('document_queue_http_responses')
        .select('*')
        .limit(10)
      
      if (error) throw error
      setHttpResponses(data || [])
    } catch (error) {
      console.error('Failed to load HTTP responses:', error)
    }
  }

  // Start a manual polling consumer (for testing purposes only)
  const startManualConsumer = () => {
    if (isManualConsumerRunning) return

    const stopFn = DocumentQueueManager.startPollingConsumer(
      3000, // Poll every 3 seconds
      5,    // Process up to 5 messages at a time
      (messages) => {
        console.log('Manual consumer processed messages:', messages)
        // Refresh stats after processing
        loadQueueStats()
      },
      (error) => {
        console.error('Manual consumer error:', error)
      }
    )

    setConsumerStop(() => stopFn)
    setIsManualConsumerRunning(true)
  }

  // Stop the manual polling consumer
  const stopManualConsumer = () => {
    if (consumerStop) {
      consumerStop()
      setConsumerStop(null)
    }
    setIsManualConsumerRunning(false)
  }

  // Trigger the edge function consumer
  const triggerEdgeConsumer = async () => {
    setLoading(true)
    try {
      const result = await DocumentQueueManager.triggerConsumer(5, 30)
      console.log('Edge consumer result:', result)
      await loadQueueStats()
    } catch (error) {
      console.error('Failed to trigger edge consumer:', error)
    } finally {
      setLoading(false)
    }
  }

  // Load initial data
  useEffect(() => {
    loadQueueStats()
    loadRecentMessages()
    loadScheduledJobs()
    loadHttpResponses()
  }, [])

  // Auto-refresh stats every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadQueueStats()
      loadScheduledJobs()
      loadHttpResponses()
    }, 10000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Document Queue Monitor</h2>
        <div className="flex gap-2">
          <Button
            onClick={loadQueueStats}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Server-side Processing Notice */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Document queue processing happens server-side via edge functions. 
          Use the "Trigger Edge Function Consumer" button below to process messages.
        </AlertDescription>
      </Alert>

      {/* Queue Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {queueStats.map((stat) => (
          <Card key={stat.queue_name}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.queue_name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Queue Length:</span>
                  <Badge variant={stat.queue_length > 0 ? "destructive" : "secondary"}>
                    {stat.queue_length}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Messages:</span>
                  <span className="text-sm font-medium">{stat.total_messages}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Oldest Message:</span>
                  <span className="text-xs text-muted-foreground">
                    {stat.oldest_msg_age_sec > 0 ? `${Math.round(stat.oldest_msg_age_sec)}s ago` : 'N/A'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Scheduled Jobs Status */}
      <Card>
        <CardHeader>
          <CardTitle>Scheduled Jobs</CardTitle>
          <CardDescription>
            Automatic queue processing jobs running in the background
          </CardDescription>
        </CardHeader>
        <CardContent>
          {scheduledJobs.length === 0 ? (
            <div className="flex items-center gap-2 text-amber-600">
              <Clock className="h-4 w-4" />
              <span className="text-sm">No scheduled jobs found - run the setup script to enable automatic processing</span>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduledJobs.map((job) => (
                <div key={job.jobid} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant={job.active ? "default" : "secondary"}>
                        {job.active ? (
                          <CheckCircle className="h-3 w-3 mr-1" />
                        ) : (
                          <XCircle className="h-3 w-3 mr-1" />
                        )}
                        {job.active ? 'Active' : 'Inactive'}
                      </Badge>
                      <span className="font-medium">{job.jobname}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Schedule: {job.schedule}
                    </p>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Job ID: {job.jobid}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consumer Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Manual Queue Processing</CardTitle>
          <CardDescription>
            Trigger server-side message processing manually or test with client-side consumer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <Button
              onClick={triggerEdgeConsumer}
              disabled={loading}
              variant="default"
              size="sm"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Trigger Edge Function Consumer
            </Button>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-2">Manual Testing Consumer</h4>
            <div className="flex items-center gap-2">              
              <Button
                onClick={isManualConsumerRunning ? stopManualConsumer : startManualConsumer}
                variant={isManualConsumerRunning ? "destructive" : "outline"}
                size="sm"
              >
                {isManualConsumerRunning ? (
                  <>
                    <Square className="h-4 w-4 mr-2" />
                    Stop Manual Consumer
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Start Manual Consumer
                  </>
                )}
              </Button>
              
              {isManualConsumerRunning && (
                <Badge variant="destructive">
                  Manual Running
                </Badge>
              )}
            </div>
          </div>

          <div className="mt-4 text-sm text-muted-foreground">
            <p>
              <strong>Edge Function Consumer:</strong> Recommended - runs server-side and processes messages in batches.
            </p>
            <p>
              <strong>Manual Consumer:</strong> For testing only - runs client-side (not recommended for production).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* HTTP Responses */}
      <Card>
        <CardHeader>
          <CardTitle>Edge Function Responses</CardTitle>
          <CardDescription>
            Recent HTTP responses from edge function calls
          </CardDescription>
        </CardHeader>
        <CardContent>
          {httpResponses.length === 0 ? (
            <p className="text-gray-500 text-sm">No HTTP responses recorded yet</p>
          ) : (
            <div className="space-y-2">
              {httpResponses.slice(0, 5).map((response) => (
                <div key={response.id} className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      response.status_code >= 200 && response.status_code < 300 ? "default" : "destructive"
                    }>
                      {response.status_code || 'Pending'}
                    </Badge>
                    <span className="text-sm font-medium">{response.method}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(response.created_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Messages */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Messages</CardTitle>
          <CardDescription>
            Latest document change messages in the queue
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentMessages.length === 0 ? (
            <p className="text-muted-foreground">No messages in queue</p>
          ) : (
            <div className="space-y-3">
              {recentMessages.map((message) => (
                <div key={message.msg_id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">ID: {message.msg_id}</Badge>
                      <Badge variant={
                        message.change_type === 'created' ? 'default' :
                        message.change_type === 'updated' ? 'secondary' : 'destructive'
                      }>
                        {message.change_type}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(message.timestamp * 1000).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm">
                    <p><strong>Document ID:</strong> {message.document_id}</p>
                    <p><strong>Changed By:</strong> {message.changed_by}</p>
                    <p><strong>Read Count:</strong> {message.read_ct}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
} 